import React, { ReactElement } from 'react';
import { Button as BaseButton, CircularProgress } from '@mui/material';

import css from './Button.module.css';

export type ButtonProps = {
  id: string;
  name?: string;
  size?: 'small' | 'medium' | 'large';
  autoFocus?: boolean;
  label?: string;
  type?: 'button' | 'submit' | 'reset';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  color?: 'primary' | 'secondary' | 'inherit' | undefined;
  tabIndex?: number;
  ariaHidden?: boolean;
  buttonBaseTag?: 'button' | 'a';
  variantStyle?: 'text' | 'outlined' | 'contained';
  classes?: Record<string, string>;
  prefixIcon?: ReactElement | null;
  onClick?: () => void;
  testId?: string;
  minWidth?: number | string;
  maxWidth?: number | string;
  href?: string;
  linkTarget?: string;
};

const Button = ({
  id,
  name = '',
  size = 'medium',
  autoFocus = false,
  label = '',
  type = 'button',
  fullWidth = false,
  disabled = false,
  loading = false,
  color = 'primary',
  tabIndex = 0,
  ariaHidden = false,
  buttonBaseTag = 'button',
  variantStyle = 'text',
  prefixIcon = null,
  classes,
  onClick,
  testId,
  minWidth,
  maxWidth,
  href,
  linkTarget
}: ButtonProps): ReactElement => (
  <BaseButton
    id={id}
    type={type}
    className={css.button}
    fullWidth={fullWidth}
    aria-label={label}
    autoFocus={autoFocus}
    tabIndex={tabIndex || 0}
    onClick={onClick}
    color={color}
    variant={variantStyle}
    classes={{
      ...classes,
      sizeLarge: css.sizeLarge,
      containedSecondary: css.containedSecondary,
      outlinedSecondary: css.outlinedSecondary
    }}
    startIcon={prefixIcon}
    data-testid={testId || `testid-input-${name || id || label || 'button'}`}
    size={size}
    disabled={disabled || loading || false}
    disableRipple
    aria-hidden={ariaHidden}
    component={buttonBaseTag}
    href={href}
    target={linkTarget}
    style={{
      minWidth,
      maxWidth
    }}
    role={buttonBaseTag === 'a' ? 'link' : 'button'}
  >
    {loading ? <CircularProgress aria-live="assertive" aria-label="Loading" size={18} /> : null}
    &nbsp;
    {label}
  </BaseButton>
);

export default Button;
